import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";

import { setRuntimeAuthToken, setActiveArtistId, AUTH_EVENTS } from "@/api/client";

type AuthContextValue = {
  isAuthenticated: boolean;
  accessToken: string | null;
  profile: Record<string, unknown> | null;
  completeLogin: (token: string, profile: Record<string, unknown>) => void;
  signout: () => void;
};

const ACCESS_TOKEN_KEY = "americana:agents:token";
const PROFILE_KEY = "americana:agents:profile";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const isBrowser = typeof window !== "undefined";

function readStoredProfile(): Record<string, unknown> | null {
  if (!isBrowser) {
    return null;
  }
  const stored = window.localStorage.getItem(PROFILE_KEY);
  if (!stored) {
    return null;
  }
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    if (!isBrowser) return null;
    const stored = window.localStorage.getItem(ACCESS_TOKEN_KEY) || window.localStorage.getItem("auth_token");
    if (stored) {
      setRuntimeAuthToken(stored);
    }
    return stored;
  });
  const [profile, setProfile] = useState<Record<string, unknown> | null>(() => readStoredProfile());

  useEffect(() => {
    if (!isBrowser) return;
    if (accessToken) {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      setRuntimeAuthToken(accessToken);
    } else {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      setRuntimeAuthToken("");
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isBrowser) return;
    if (profile) {
      window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } else {
      window.localStorage.removeItem(PROFILE_KEY);
    }
  }, [profile]);

  const completeLogin = useCallback((token: string, userProfile: Record<string, unknown>) => {
    setAccessToken(token);
    setProfile(userProfile);
    setRuntimeAuthToken(token);
  }, []);

  const signout = useCallback(() => {
    // Revoke versioned application and collector sessions before clearing local state.
    if (accessToken) void fetch("/auth/logout", {method:"POST", headers:{Authorization:`Bearer ${accessToken}`}, keepalive:true}).catch(() => {
      console.warn("Server logout unavailable; short-lived sessions may remain valid until expiry.");
    });
    setAccessToken(null);
    setProfile(null);
    setRuntimeAuthToken("");
    if (isBrowser) {
      window.localStorage.removeItem("auth_token");
    }
    setActiveArtistId("");
  }, [accessToken]);

  useEffect(() => {
    if (!isBrowser) return;
    const handleUnauthorized = () => {
      if (!accessToken) return;
      signout();
    };
    window.addEventListener(AUTH_EVENTS.unauthorized, handleUnauthorized);
    return () => {
      window.removeEventListener(AUTH_EVENTS.unauthorized, handleUnauthorized);
    };
  }, [signout, accessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accessToken,
      profile,
      isAuthenticated: Boolean(accessToken),
      completeLogin,
      signout,
    }),
    [accessToken, profile, completeLogin, signout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export { AuthContext };
